import React, {
  Children,
  PropsWithChildren,
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from "react";

import { css, cx } from "@emotion/css";
import MuiSelect, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { type Options, findOptionsFromChildren, selectOption } from "@/uiKit/atoms/inputs/SelectUtils.ts";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

export type BaseSelectInputProps = PropsWithChildren<{
  initialValue?: string;
  value?: string;
  label?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: (value: string | undefined, event: SelectChangeEvent, child: React.ReactNode) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onClose?: (event: React.SyntheticEvent) => void;
  onOpen?: (event: React.SyntheticEvent) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}>;

export type SelectInputProps = AtomProps<BaseSelectInputProps, StyleClasses<"root" | "disabled" | "select" | "error">>;

export const SelectInput: React.FC<SelectInputProps> = ({
  id,
  value,
  initialValue,
  label,
  onChange,
  className,
  classes,
  children,
  ...props
}) => {
  const options: Options = useMemo(() => findOptionsFromChildren(children), [children]);
  const isInitialValueValid = initialValue && initialValue in options;
  const [internalValue, setInternalValue] = useState<string | undefined>(
    value || (isInitialValueValid && initialValue) || undefined,
  );
  const onChangeHandler = useCallback(
    (event: SelectChangeEvent, component: React.ReactNode) => {
      const selection = selectOption(component);
      if (selection) {
        setInternalValue(selection.newValue);
        onChange?.(selection.newValue, event, component);
      }
    },
    [onChange],
  );

  const styles = useStyles(makeStyles);

  return (
    <MuiSelect<string>
      id={id}
      value={value || internalValue || ""}
      onChange={onChangeHandler}
      className={cx(styles.root, className)}
      displayEmpty
      renderValue={value => {
        if (value) {
          if (value in options) {
            return options[value];
          } else {
            return null;
          }
        }
        if (label) {
          return <Typography className={styles.label}>{label}</Typography>;
        }
        return null;
      }}
      classes={{
        root: classes?.root,
        select: classes?.select,
        disabled: classes?.disabled,
        error: classes?.error,
      }}
      MenuProps={{
        classes: {
          paper: styles.paper,
        },
      }}
      {...props}
    >
      {Children.map(children, child =>
        isValidElement<{ id?: string; value?: string }>(child) && child.props.value !== undefined
          ? cloneElement(child, { id: child.props.id ?? suffixId(id, `option-${child.props.value}`) })
          : child,
      )}
    </MuiSelect>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    minHeight: theme.spacing(16),
    backgroundColor: theme.palette.grey[100],
    border: "none",
    borderRadius: theme.spacing(12),
    ".MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiInputBase-input": {
      minWidth: 0,
      overflow: "clip",
      flex: "1 1 auto",
    },
    "& .MuiSelect-select": {
      padding: theme.spacing(2, 8),
    },
    "&.Mui-error": {
      outlineWidth: theme.spacing(0.7),
      outlineOffset: theme.spacing(-0.7),
      outlineColor: theme.palette.error.main,
      outlineStyle: "solid",
      outlineRadius: theme.spacing(12),
      color: theme.palette.error.main,
      fill: theme.palette.error.main,
      "& .MuiSelect-icon": {
        color: theme.palette.error.main,
      },
    },
  }),
  label: css({
    color: theme.palette.text.secondary,
  }),
  emptyInput: css({
    display: "none",
  }),
  paper: css({
    borderRadius: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    padding: 0,
    "& > .MuiList-root": {
      padding: 0,
    },
  }),
});
